import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import http, { BASE_URL, HttpSuccessOrErrorType } from '../http';
import type {
    AnagraficaOcrConfirmFieldPayload,
    AnagraficaOcrListQuery,
    AnagraficaOcrParseResult,
    AnagraficaOcrSessionListResult,
} from '../../component/archive-form/anagrafica-ocr';
import _ from 'lodash';

/** 逐项确认的一条字段（含 manual 时的 value）。 */
export type AnagraficaOcrConfirmItem = AnagraficaOcrConfirmFieldPayload;

/**
 * 分页查询 OCR 会话列表（超管对照历史识别）。
 * @param query 可选 scope / created_by / page / page_size 等
 */
async function list_anagrafica_ocr(
    query: AnagraficaOcrListQuery = {},
): Promise<HttpSuccessOrErrorType<AnagraficaOcrSessionListResult>> {
    return await http.get('/anagrafica/ocr', { params: query });
}

/**
 * 上传登记文件并创建 OCR 会话。
 * 文件字段名按当前 OAS 使用 `1`。
 * @param file 本地 PDF 或图片
 * @param scope 本次识别的主体；新增餐厅固定为 restaurant
 */
async function parse_anagrafica_ocr(
    file: File,
    scope: 'restaurant' | 'group' = 'restaurant',
): Promise<HttpSuccessOrErrorType<AnagraficaOcrParseResult>> {
    const formData = new FormData();
    formData.append('1', file);
    formData.append('scope', scope);
    return await http.post('/anagrafica/ocr/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
}

/**
 * 一键确认高置信且非冲突字段。无请求体。
 * @param sessionId OCR 会话 ID
 */
async function confirm_anagrafica_ocr_high(
    sessionId: string,
): Promise<HttpSuccessOrErrorType<Record<string, unknown>>> {
    return await http.post(`/anagrafica/ocr/${sessionId}/confirm-high`);
}

/**
 * 提交已核对字段的采纳侧。
 * @param sessionId OCR 会话 ID
 * @param fields 已核对字段
 */
async function confirm_anagrafica_ocr(
    sessionId: string,
    fields: AnagraficaOcrConfirmItem[],
): Promise<HttpSuccessOrErrorType<Record<string, unknown>>> {
    return await http.post(`/anagrafica/ocr/${sessionId}/confirm`, { fields });
}

/**
 * 删除未保存的 OCR 会话。
 * @param sessionId OCR 会话 ID
 */
async function delete_anagrafica_ocr_session(
    sessionId: string,
): Promise<HttpSuccessOrErrorType<Record<string, unknown>>> {
    return await http.delete(`/anagrafica/ocr/${sessionId}`);
}

/**
 * 页面卸载时删除会话。keepalive 让请求在刷新或关闭后仍能发出。
 * @param sessionId OCR 会话 ID
 */
function delete_anagrafica_ocr_session_keepalive(sessionId: string): void {
    const token = LSM.get(LOCAL_STORAGE_KEYS.TOKEN) || {};
    const accessToken = _.get(token, 'accessToken') ?? '';
    void fetch(`${BASE_URL}/anagrafica/ocr/${sessionId}`, {
        method: 'DELETE',
        keepalive: true,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'authorization-type': 'BASIC_AUTH',
        },
    }).catch(() => undefined);
}

export default {
    parse_anagrafica_ocr,
    list_anagrafica_ocr,
    confirm_anagrafica_ocr_high,
    confirm_anagrafica_ocr,
    delete_anagrafica_ocr_session,
    delete_anagrafica_ocr_session_keepalive,
};
